import Logo from "../components/common/Logo";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Navigate, useNavigate } from "react-router-dom";
import {
  useCheckoutMutation,
  useFetchOrderFeesQuery,
  useGetAddressesQuery,
  useTypedSelector,
} from "../store";
import {
  CartItem,
  DropdownOption,
  FeeTypeText,
  OrderCheckout,
  OrderType,
} from "../types";
import ItemRecapCard from "../components/items/ItemRecapCard";
import { cleanCartItems, formatPrice } from "../utils/helpers";
import Button from "../components/common/Button";
import { useEffect, useState } from "react";
import { loadStripe } from "@stripe/stripe-js";
import { Elements } from "@stripe/react-stripe-js";
import CheckoutForm from "../forms/CheckoutForm";
import DropDown from "../components/common/DropDown";
import AddressModal from "../forms/AddressFormModal";

const stripePromise = await loadStripe(
  process.env.REACT_APP_STRIPE_PUBLICHABLE_KEY!
);

const CheckoutWrapper = () => {
  const { orderType } = useTypedSelector((state) => state.session.search);
  const cart = useTypedSelector((state) => state.cart);
  const { data: userAddresses } = useGetAddressesQuery();
  const [selectedAddressId, setSelectedAddressId] = useState<string>("");

  useEffect(() => {
    if (userAddresses && userAddresses.length > 0) {
      if (
        selectedAddressId &&
        userAddresses.find((a) => a._id === selectedAddressId)
      ) {
        return;
      }

      const defaultAddr = userAddresses.find((addr) => addr.isDefault);
      if (defaultAddr && defaultAddr._id) {
        setSelectedAddressId(defaultAddr._id);
      } else if (userAddresses[0]._id) {
        setSelectedAddressId(userAddresses[0]._id);
      }
    }
  }, [userAddresses, selectedAddressId]);

  const selectedAddrObj = userAddresses?.find(
    (addr) => addr._id === selectedAddressId
  );
  const deliveryAddress = selectedAddrObj
    ? `${selectedAddrObj.street}, ${selectedAddrObj.zipCode} ${selectedAddrObj.city}`
    : "";

  if (!cart.items?.length || !cart.restaurantId) return <Navigate to="/" />;

  return (
    <Checkout
      restaurantId={cart.restaurantId}
      cartItems={cart.items}
      orderType={orderType}
      deliveryAddress={deliveryAddress}
      selectedAddressId={selectedAddressId}
      onAddressSelect={setSelectedAddressId}
    />
  );
};

interface CheckoutProps {
  restaurantId: string;
  cartItems: CartItem[];
  orderType: OrderType;
  deliveryAddress: string;
  selectedAddressId: string;
  onAddressSelect: (id: string) => void;
}

const Checkout: React.FC<CheckoutProps> = ({
  restaurantId,
  cartItems,
  orderType,
  deliveryAddress,
  selectedAddressId,
  onAddressSelect,
}) => {
  const [checkoutOrder] = useCheckoutMutation();
  const { data: userAddresses } = useGetAddressesQuery();
  const navigate = useNavigate();
  const [openCheckoutForm, setOpenCheckoutForm] = useState<boolean>(false);
  const [clientSecret, setClientSecret] = useState<string>("");
  const [orderId, setOrderId] = useState<string>("");
  const [isAddressModalOpen, setIsAddressModalOpen] = useState(false);

  const cartItemsTotalPrice = cartItems.reduce((acc, item) => {
    return acc + item.totalPrice * item.qty;
  }, 0);

  const { data: fees } = useFetchOrderFeesQuery(
    {
      orderPrice: cartItemsTotalPrice,
      orderType,
      address: deliveryAddress,
      restaurant: restaurantId,
    },
    { skip: !deliveryAddress && orderType === OrderType.DELIVERY }
  );

  const totalFees =
    fees?.reduce((acc, item) => {
      return acc + item.value.value;
    }, 0) || 0;

  const renderedCartItems = cartItems.map((cartItem, index) => {
    return (
      <ItemRecapCard
        key={`${cartItem._id}-${index}`}
        item={cartItem}
      />
    );
  });

  const handleCheckoutOrder = async () => {
    const body: OrderCheckout = {
      restaurantId: restaurantId,
      items: cleanCartItems(cartItems),
      totalPrice: cartItemsTotalPrice + totalFees,
      orderPrice: cartItemsTotalPrice,
      deliveryAddress,
      orderType,
    };

    const result = await checkoutOrder(body);
    if (!result.error && result.data) {
      setOrderId(result.data.orderId);
      setClientSecret(result.data.client_secret);
      setOpenCheckoutForm(true);
    }
  };

  const handleBackArrowClick = () => {
    if (openCheckoutForm) setOpenCheckoutForm(false);
    else navigate(-1);
  };

  const renderedFees = fees?.map((fee, index) => {
    return (
      <div
        className="flex justify-between text-gray-600 mx-2"
        key={index}
      >
        <div> {FeeTypeText[fee.type]} </div>
        <div> {fee.value.text} </div>
      </div>
    );
  });

  const addressOptions: DropdownOption[] =
    userAddresses?.map((addr) => ({
      label: `${addr.displayName} - ${addr.street}, ${addr.zipCode} ${addr.city}`,
      value: addr._id!,
    })) || [];

  const handleAddressChange = (field: string, value: string) => {
    onAddressSelect(value);
  };

  const renderedCart = (
    <div className="w-full max-w-2xl flex flex-col gap-6 bg-white md:rounded-2xl p-6 md:shadow-lg md:my-8 min-h-[calc(100vh-80px)] md:min-h-0">
      <div className="flex items-center justify-between pb-4 border-b border-gray-100">
        <h2 className="text-2xl font-bold text-gray-900">
          {orderType === OrderType.DELIVERY ? "Livraison" : "À emporter"}
        </h2>
      </div>

      {orderType === OrderType.DELIVERY && (
        <div className="flex flex-col gap-3">
          <div className="flex justify-between items-center">
            <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
              Adresse de livraison
            </h3>
            <Button
              onClick={() => setIsAddressModalOpen(true)}
              className="text-primary text-sm font-semibold hover:bg-primary/5 px-3 py-1 rounded transition-colors"
            >
              + Nouvelle adresse
            </Button>
          </div>

          <div className="bg-gray-50 p-4 rounded-xl border border-gray-100">
            {userAddresses && userAddresses.length > 0 ? (
              <DropDown
                id="address-select"
                value={selectedAddressId}
                options={addressOptions}
                setFieldValue={handleAddressChange}
                className="h-11 border-gray-200 shadow-sm w-full bg-white rounded-lg"
              />
            ) : (
              <div className="text-gray-500 text-sm italic py-2 text-center">
                Aucune adresse enregistrée
              </div>
            )}
          </div>
        </div>
      )}

      <div className="flex flex-col gap-4">
        <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wider">
          Votre commande
        </h3>
        <div className="flex flex-col gap-3">{renderedCartItems}</div>
      </div>

      <div className="mt-auto pt-6 border-t border-gray-100 flex flex-col gap-3">
        <div className="flex justify-between text-gray-600">
          <span>Sous-total</span>
          <span className="font-medium text-gray-900">
            {formatPrice(cartItemsTotalPrice)}
          </span>
        </div>

        {renderedFees}

        <div className="flex justify-between items-center py-4 border-t border-gray-100 mt-2">
          <span className="text-xl font-bold text-gray-900">Total</span>
          <span className="text-xl font-bold text-primary">
            {formatPrice(cartItemsTotalPrice + totalFees)}
          </span>
        </div>

        <Button
          className="w-full justify-center font-bold text-lg py-4 bg-black text-white rounded-xl hover:bg-gray-900 shadow-lg hover:shadow-xl transition-all transform active:scale-[0.99]"
          onClick={handleCheckoutOrder}
        >
          Payer {formatPrice(cartItemsTotalPrice + totalFees)}
        </Button>
      </div>

      <AddressModal
        isOpen={isAddressModalOpen}
        onClose={() => setIsAddressModalOpen(false)}
        editingAddress={null}
        onSuccess={() => setIsAddressModalOpen(false)}
      />
    </div>
  );

  const renderedCheckoutForm = (
    <div className="w-full max-w-2xl bg-white md:rounded-2xl p-6 md:shadow-lg md:my-8">
      <div className="mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Paiement sécurisé
        </h2>
        <p className="text-gray-500 text-sm">
          Complétez vos informations de paiement ci-dessous
        </p>
      </div>
      <Elements
        stripe={stripePromise}
        options={{ clientSecret }}
      >
        <CheckoutForm orderId={orderId} />
      </Elements>
    </div>
  );

  return (
    <div className="min-h-screen w-full flex flex-col bg-gray-50">
      <header className="bg-white sticky top-0 z-20 border-b border-gray-100 px-4 py-4 md:py-5 shadow-sm">
        <div className="max-w-7xl mx-auto w-full flex items-center justify-between relative">
          <button
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 transition-colors font-medium px-2 py-1 rounded-lg hover:bg-gray-100"
            onClick={handleBackArrowClick}
          >
            <IoMdArrowRoundBack className="text-xl" />
            <span className="hidden md:inline">Retour</span>
          </button>
          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2">
            <Logo className="h-12 md:h-16 w-auto" />
          </div>
          <div className="w-16"></div> {/* Spacer for centering logo */}
        </div>
      </header>

      <main className="flex-1 w-full flex justify-center p-0 md:p-6 pb-20 md:pb-6">
        {openCheckoutForm ? renderedCheckoutForm : renderedCart}
      </main>
    </div>
  );
};

export default CheckoutWrapper;
